/**
 * Tareas de todas las reuniones: lo que quedó por hacer, con quién y cuándo, y
 * un link al momento en que se dijo. Marcar como hecha se guarda en este
 * navegador (no se sincroniza todavía).
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useApp } from '../components/AppContext'
import { ErrorBox, Skeleton } from '../components/ui'
import type { TaskItem } from '../lib/api'
import { downloadText } from '../lib/exportMarkdown'
import { tasksCsv } from '../lib/exportTasks'
import { formatDate, formatDueDate, formatDuration } from '../lib/format'
import { useAsync, useDocumentTitle } from '../lib/hooks'

const HECHAS_KEY = 'eco.tareasHechas'

function leerHechas(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(HECHAS_KEY) ?? '[]') as string[])
  } catch {
    return new Set()
  }
}

export function Tasks() {
  useDocumentTitle('Tareas')
  const { api } = useApp()
  const [dias, setDias] = useState(30)
  const [soloMias, setSoloMias] = useState(false)
  const [verHechas, setVerHechas] = useState(false)
  const [hechas, setHechas] = useState(leerHechas)
  const tasks = useAsync(() => api.tasks(dias), [api, dias])

  function toggle(id: string) {
    setHechas((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      try {
        // Solo las últimas 2000 (las más viejas dejan de importar)
        localStorage.setItem(HECHAS_KEY, JSON.stringify([...next].slice(-2000)))
      } catch {
        /* sin almacenamiento: queda solo en esta sesión */
      }
      return next
    })
  }

  const visibles = useMemo(
    () =>
      (tasks.data ?? []).filter(
        (t) =>
          // «Yo» es quien grabó: en una reunión de otra persona del equipo no es mía
          (!soloMias || (t.responsableEtiqueta === 'Yo' && t.propia !== false)) &&
          (verHechas || !hechas.has(t.id))
      ),
    [tasks.data, soloMias, verHechas, hechas]
  )
  const porReunion = useMemo(() => {
    const m = new Map<string, TaskItem[]>()
    for (const t of visibles) m.set(t.recordingId, [...(m.get(t.recordingId) ?? []), t])
    return [...m.values()]
  }, [visibles])

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="grow">Tareas</h1>
      </div>
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <div className="filters" role="group" aria-label="De quién">
          <button
            className={`chip ${!soloMias ? 'on' : ''}`}
            aria-pressed={!soloMias}
            onClick={() => setSoloMias(false)}
          >
            Todas
          </button>
          <button
            className={`chip ${soloMias ? 'on' : ''}`}
            aria-pressed={soloMias}
            onClick={() => setSoloMias(true)}
          >
            Mías
          </button>
        </div>
        <label className="row" style={{ gap: 6, fontSize: 13 }}>
          <span className="faint">De los últimos</span>
          <select value={dias} onChange={(e) => setDias(Number(e.target.value))}>
            <option value={7}>7 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
          </select>
        </label>
        <button
          className="btn sm ghost"
          disabled={!visibles.length}
          onClick={() =>
            downloadText(
              `tareas-${new Date().toISOString().slice(0, 10)}.csv`,
              tasksCsv(visibles, hechas),
              'text/csv'
            )
          }
        >
          Descargar CSV
        </button>
        <label className="check" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={verHechas}
            onChange={(e) => setVerHechas(e.target.checked)}
          />
          <span>Mostrar hechas</span>
        </label>
      </div>

      {tasks.error ? (
        <ErrorBox error={tasks.error} onRetry={() => void tasks.reload()} />
      ) : !tasks.data ? (
        <div className="stack">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} h={52} />
          ))}
        </div>
      ) : porReunion.length === 0 ? (
        <div className="empty">
          <h2>Sin tareas pendientes</h2>
          <p className="muted">
            {soloMias
              ? 'No hay tareas asignadas a ti en este período.'
              : 'Las tareas que se acuerden en tus reuniones aparecerán aquí.'}
          </p>
        </div>
      ) : (
        <div className="stack">
          {porReunion.map((items) => (
            <section key={items[0]!.recordingId} className="card stack" style={{ gap: 6 }}>
              <h2 style={{ fontSize: 14, margin: 0 }}>
                <Link to={`/r/${items[0]!.recordingId}`}>{items[0]!.titulo}</Link>{' '}
                <span className="faint" style={{ fontWeight: 400 }}>
                  · {formatDate(items[0]!.fechaReunion)}
                </span>
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="stack">
                {items.map((t) => {
                  const quien =
                    t.responsableMencionado ??
                    t.responsableNombre ??
                    (t.responsableEtiqueta === 'Yo' && t.propia === false
                      ? 'quien grabó'
                      : t.responsableEtiqueta)
                  return (
                    <li key={t.id} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
                      <input
                        type="checkbox"
                        aria-label={`Marcar como hecha: ${t.descripcion}`}
                        checked={hechas.has(t.id)}
                        onChange={() => toggle(t.id)}
                        style={{ marginTop: 3 }}
                      />
                      <span
                        className="grow"
                        style={hechas.has(t.id) ? { textDecoration: 'line-through' } : {}}
                      >
                        {t.descripcion}
                        {(quien || t.fecha) && (
                          <span className="faint" style={{ fontSize: 12.5 }}>
                            {' '}
                            ·{' '}
                            {[quien, t.fecha && formatDueDate(t.fecha)].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                      {t.inicioMs !== null && (
                        <Link
                          className="ev"
                          to={`/r/${t.recordingId}?t=${t.inicioMs}`}
                          title="Ir al momento en que se dijo"
                        >
                          {formatDuration(t.inicioMs)}
                        </Link>
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
